import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { getPhotoList, createPhotoBoard } from "../../api/const";
import Photo_new from "./Photo_new";
import SidePanel from "../../components/common/layout/SidePanel";
import SearchBar from "../../components/common/board/SearchFilterBar";
import BoardPagination from "../../components/common/board/BoardPagination";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import PageHeader from "../../components/common/ui/PageHeader";

export default function Photo() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // 검색과 필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // 로딩 / 오류
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 선택 모드
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // 사진등록 모달
  const [isModalOpen, setIsModalOpen] = useState(false);

  /** =============================
   * 📌 사진 목록 가져오기
   * ============================= */
  const fetchPhotos = async () => {
    if (!projectId) return;

    setLoading(true);
    setError("");

    try {
      const params = {
        [searchField]: q,
        start_date: startDate,
        end_date: endDate,
        page,
        page_size: pageSize,
      };

      const res = await getPhotoList(projectId, params);
      setPhotos(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (err) {
      console.error(err);
      setError("사진 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [projectId, page, pageSize, searchTrigger]);

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  /** 선택 토글 */
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((p) => p !== id)
        : [...prev, id]
    );
  };

  /** 사진대지 생성 */
  const handleCreateBoard = async () => {
    if (selectedIds.length === 0) return alert("사진을 먼저 선택하세요!");

    try {
      const dim = Math.ceil(Math.sqrt(selectedIds.length));
      const newBoard = await createPhotoBoard({
        project: projectId,
        title: "새 사진대지",
        photo_ids: selectedIds,
        grid_x: dim,
        grid_y: Math.ceil(selectedIds.length / dim),
        date: new Date().toISOString().split("T")[0],
      });

      alert("사진대지가 생성되었습니다!");
      navigate(`/project/${projectId}/const/photoboard/view/${newBoard.id}`);
    } catch (error) {
      console.error("사진대지 생성 실패:", error);
      alert("생성 실패");
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-6 bg-gray-50 rounded-2xl space-y-6">

      {/* =============================
          📌 페이지 헤더
      ============================= */}
      <PageHeader>시공사진</PageHeader>

      {/* =============================
          📌 툴바 (검색 + 버튼)
      ============================= */}
      <BoardToolbar
        right={
          <>
            <SearchBar
              q={q}
              onChangeQ={setQ}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              onSearch={handleSearch}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
              searchFieldOptions={[{ value: "title", label: "제목" }]}
            />

            <div className="flex gap-2 ml-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-create"
              >
                새 사진 등록
              </button>

              <button
                onClick={() => {
                  setSelectMode((v) => !v);
                  setSelectedIds([]);
                }}
                className={`btn-basic ${selectMode ? "border-red-500 text-red-600" : ""
                  }`}
              >
                {selectMode ? "선택 종료" : "사진 선택"}
              </button>

              {selectMode && (
                <button className="btn-save" onClick={handleCreateBoard}>
                  사진대지 생성
                </button>
              )}
            </div>
          </>
        }
      />

      {/* =============================
          📌 사진 그리드
      ============================= */}
      <div className="page-box">
        {loading ? (
          <div className="text-center py-12 text-muted-sm">로딩 중...</div>
        ) : photos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted mb-3">등록된 사진이 없습니다.</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-blue-600 hover:underline"
            >
              첫 번째 사진을 등록해보세요
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className={`bg-gray-50 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer relative ${selectedIds.includes(photo.id) ? "ring-2 ring-blue-500" : ""
                  }`}
                onClick={() =>
                  selectMode
                    ? toggleSelect(photo.id)
                    : setSelectedPhoto(photo)
                }
              >
                {/* 선택 모드 체크박스 */}
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(photo.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(photo.id)}
                    className="absolute top-2 left-2 w-4 h-4"
                  />
                )}

                <div className="w-full h-44 bg-gray-100 flex items-center justify-center">
                  <img
                    src={photo.file_url}
                    alt={photo.title}
                    className="max-h-full object-contain"
                  />
                </div>

                <div className="p-2">
                  <h3 className="text-body-bold truncate">{photo.title}</h3>
                  <p className="text-xs text-gray-500">
                    {new Date(photo.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* =============================
          📌 페이지네이션
      ============================= */}
      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      />

      {/* =============================
          📌 사이드 패널 (사진 상세보기)
      ============================= */}
      <SidePanel
        isOpen={Boolean(selectedPhoto) && !selectMode}
        onClose={() => setSelectedPhoto(null)}
        title={selectedPhoto?.title}
        width={600}
        side="right"
        footer={
          <div className="pb-6">
            <button
              onClick={() => navigate(`view/${selectedPhoto?.id}`)}
              className="btn-create w-full"
            >
              상세보기
            </button>
          </div>
        }
      >
        {selectedPhoto && (
          <div className="p-4">
            <img
              src={selectedPhoto.file_url}
              alt={selectedPhoto.title}
              className="max-h-[500px] mx-auto object-contain rounded-lg"
            />
          </div>
        )}
      </SidePanel>

      {/* =============================
          📌 사진 등록 모달
      ============================= */}
      <Photo_new
        projectId={projectId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={fetchPhotos}
      />
    </div>
  );
}
